<template>
  <div id="pageColaboradorDealers">
    <v-card class="mb-5">
      <v-toolbar color="primary" dark flat>
        <v-icon class="mr-2">mdi-store</v-icon>
        <v-toolbar-title>Dealers asociados al colaborador</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn @click="showAddDealerDialog = true" icon>
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="pa-0">
        <v-data-table
          :headers="headers"
          :items="dealersAsociados"
          :loading="loading"
          :footer-props="footer"
          class="elevation-1"
          hide-default-footer
        >
          <template v-slot:item="{ item }">
            <tr>
              <td style="white-space: nowrap;">
                <div class="d-flex align-center">
                  <v-avatar size="32" class="mr-3">
                    <img v-if="item.imageUrl && item.imageUrl.url" :src="item.imageUrl.url" alt="Logo">
                    <v-icon v-else color="grey">mdi-store</v-icon>
                  </v-avatar>
                  {{ item.name }}
                </div>
              </td>
              <td style="white-space: nowrap;">{{ item.email }}</td>
              <td style="white-space: nowrap;">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn 
                      icon 
                      color="red" 
                      @click="removeDealerAssociation(item)" 
                      v-on="on"
                      :loading="loading"
                    >
                      <v-icon size="20">mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Eliminar asociación</span>
                </v-tooltip>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Diálogo para agregar dealer -->
    <v-dialog v-model="showAddDealerDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="headline">
          <v-icon left>mdi-plus</v-icon>
          Agregar dealer al colaborador
        </v-card-title>
        
        <v-card-text>
          <v-autocomplete
            v-model="selectedDealerToAdd"
            :items="availableDealers"
            :loading="loadingAvailableDealers"
            :search-input.sync="dealerSearchInput"
            item-text="name"
            item-value="id"
            label="Seleccionar dealer"
            prepend-inner-icon="mdi-store"
            return-object
            clearable
            outlined
            hide-no-data
            hide-selected
          >
            <template v-slot:selection="{ item }">
              <div class="d-flex align-center">
                <v-avatar size="24" class="mr-2">
                  <img v-if="item.imageUrl && item.imageUrl.url" :src="item.imageUrl.url" alt="Logo">
                  <v-icon v-else small color="grey">mdi-store</v-icon>
                </v-avatar>
                {{ item.name }}
              </div>
            </template>
            
            <template v-slot:item="{ item }">
              <div class="d-flex align-center">
                <v-avatar size="32" class="mr-3">
                  <img v-if="item.imageUrl && item.imageUrl.url" :src="item.imageUrl.url" alt="Logo">
                  <v-icon v-else color="grey">mdi-store</v-icon>
                </v-avatar>
                <div>
                  <div>{{ item.name }}</div>
                  <div class="text-caption text--secondary">{{ item.email }}</div>
                </div>
              </div>
            </template>
          </v-autocomplete>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeAddDealerDialog">Cancelar</v-btn>
          <v-btn 
            color="primary" 
            :disabled="!selectedDealerToAdd" 
            :loading="addingDealer"
            @click="addDealerAssociation"
          >
            Agregar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import _ from 'lodash'
import DealerService from '../../services/DealerService'

export default {
  props: {
    colaboradorId: {
      type: Number,
      required: true
    }
  },

  data: () => ({
    loading: false,
    addingDealer: false,
    loadingAvailableDealers: false,

    headers: [
      { text: 'Nombre', value: 'name' },
      { text: 'Email', value: 'email' },
      { text: 'Acciones', value: 'actions', sortable: false, align: 'right' }
    ],

    footer: {
      itemsPerPageOptions: [10, 20, 30, 40, 50]
    },

    dealersAsociados: [],
    
    // Para agregar dealers
    showAddDealerDialog: false,
    availableDealers: [],
    selectedDealerToAdd: null,
    dealerSearchInput: null
  }),

  async created () {
    this.loadDealersAsociados()
    this.loadAvailableDealers()
    
    // Debounced search para dealers
    this.debouncedLoadDealers = _.debounce(this.loadAvailableDealers, 300)
  },

  methods: {
    async loadDealersAsociados () {
      this.loading = true
      try {
        const response = await DealerService.view({
          id: this.colaboradorId,
          expand: 'dealersAsociados'
        })
        this.dealersAsociados = response.data.data.dealersAsociados || []
      } catch (error) {
        console.error(error)
        this.$toast.error('Ocurrió un problema al intentar obtener la lista de dealers asociados')
      } finally {
        this.loading = false
      }
    },

    async loadAvailableDealers () {
      try {
        this.loadingAvailableDealers = true
        const response = await DealerService.get({
          'filter[is_colaborador]': false, // Solo dealers que NO sean colaboradores
          'per-page': 500,
          search: this.dealerSearchInput || ''
        })

        console.log('Available dealers:', response.data)

        // Filtrar dealers que ya están asociados
        const associatedIds = this.dealersAsociados.map(dealer => dealer.id)
        this.availableDealers = (response.data.data || []).filter(dealer => 
          !associatedIds.includes(dealer.id)
        )
      } catch (error) {
        console.error('Error al cargar dealers disponibles:', error)
      } finally {
        this.loadingAvailableDealers = false
      }
    },

    async addDealerAssociation () {
      if (!this.selectedDealerToAdd) return
      
      try {
        this.addingDealer = true
        
        await DealerService.assignDealers(this.colaboradorId, {
          dealer_ids: [this.selectedDealerToAdd.id]
        })
        
        this.$toast.success('Dealer agregado exitosamente')
        this.closeAddDealerDialog()
        
        // Recargar la lista de dealers asociados
        await this.loadDealersAsociados()
        
        // Recargar dealers disponibles para actualizar la lista
        await this.loadAvailableDealers()
      } catch (error) {
        console.error('Error al agregar dealer:', error)
        this.$toast.error('Error al agregar dealer')
      } finally {
        this.addingDealer = false
      }
    },

    async removeDealerAssociation (dealer) {
      this.$confirm(`¿Está seguro que desea eliminar la asociación con "${dealer.name}"?`).then(async (result) => {
        if (result) {
          try {
            this.loading = true
            
            // Llamar al endpoint para desasociar con el formato correcto
            await DealerService.removeDealers(this.colaboradorId, {
              dealer_ids: [dealer.id]
            })
            
            // Remover del array local
            const index = this.dealersAsociados.findIndex(d => d.id === dealer.id)
            if (index > -1) {
              this.dealersAsociados.splice(index, 1)
            }
            
            this.$toast.success('Dealer desasociado exitosamente')
            
            // Recargar dealers disponibles
            await this.loadAvailableDealers()
          } catch (error) {
            console.error('Error al desasociar dealer:', error)
            this.$toast.error('Error al desasociar dealer')
          } finally {
            this.loading = false
          }
        }
      })
    },

    closeAddDealerDialog () {
      this.showAddDealerDialog = false
      this.selectedDealerToAdd = null
      this.dealerSearchInput = null
    }
  },

  watch: {
    dealerSearchInput () {
      this.debouncedLoadDealers()
    }
  }
}
</script>

<style scoped>
</style>
